import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import BackgroundMusicController from '../components/BackgroundMusicController'
import Footer from '../components/Footer'
import ScrollingBanner from '../components/ScrollingBanner'
import AuthModal from '../modules/auth/components/AuthModal'
import ReferralBanner from '../modules/auth/components/ReferralBanner'
import { useAuth } from '../modules/auth/hooks/useAuth'
import CustomerLeftSideBar from '../components/navigation/CustomerLeftSideBar'
import CustomerRightSideBar from '../components/navigation/CustomerRightSideBar'
import { customerNavItems } from '../components/navigation/navConfig'
import { CustomerLeftSidebarSlotProvider, type CustomerLeftSidebarSlotValue } from './customerLeftSidebarSlot'

export default function CustomerLayout() {
  const { user, isInitializing } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [authOpen, setAuthOpen] = useState(false)
  const [authInitialMode, setAuthInitialMode] = useState<'login' | 'register'>('login')
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false)
  const bannerWrapRef = useRef<HTMLDivElement | null>(null)
  const [leftSidebarSlot, setLeftSidebarSlot] = useState<CustomerLeftSidebarSlotValue>({ content: null })
  const setLeftSidebar = useCallback((next: CustomerLeftSidebarSlotValue) => setLeftSidebarSlot(next), [])
  const clearLeftSidebar = useCallback(() => setLeftSidebarSlot({ content: null }), [])
  const leftSidebarSlotApi = useMemo(() => ({ setLeftSidebar, clearLeftSidebar }), [setLeftSidebar, clearLeftSidebar])

  const activeKey = useMemo(() => {
    const key = (location.pathname.split('/').filter(Boolean)[0] || 'products').trim()
    return key || 'products'
  }, [location.pathname])

  const onNavigateKey = (key: string) => {
    if (isInitializing) return
    const item = customerNavItems.find(i => i.key === key)
    if (!item) return
    if (item.requiresAuth && !user) {
      setAuthOpen(true)
      return
    }
    navigate(item.path)
    setRightSidebarOpen(false)
  }

  useEffect(() => {
    const wrap = bannerWrapRef.current
    const setHeight = (h: number) => {
      try {
        document.documentElement.style.setProperty('--customer-banner-height', `${Math.max(0, Math.round(h))}px`)
      } catch {
        void 0
      }
    }

    if (!wrap) {
      setHeight(0)
      return
    }

    const compute = () => setHeight(wrap.getBoundingClientRect().height)
    compute()

    const ro = new ResizeObserver(() => compute())
    ro.observe(wrap)
    return () => {
      ro.disconnect()
      setHeight(0)
    }
  }, [])

  useEffect(() => {
    const stored = window.localStorage.getItem('digigo-theme')
    const shouldBeDark = stored
      ? stored === 'dark'
      : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    document.documentElement.classList.toggle('dark', shouldBeDark)

    const onThemeChanged = (ev: Event) => {
      const nextIsDark = (ev as CustomEvent<{ isDark: boolean }>).detail?.isDark
      if (typeof nextIsDark === 'boolean') document.documentElement.classList.toggle('dark', nextIsDark)
    }
    window.addEventListener('digigo-theme-changed', onThemeChanged)
    return () => window.removeEventListener('digigo-theme-changed', onThemeChanged)
  }, [])

  return (
    <CustomerLeftSidebarSlotProvider
      value={leftSidebarSlotApi}
    >
      <div className="min-h-screen app-bg text-gray-900 dark:text-gray-100">
        <Header onOpenSidebar={() => setRightSidebarOpen(true)} onOpenAuth={() => setAuthOpen(true)} />
        <BackgroundMusicController />
        <div ref={bannerWrapRef}>
          <ScrollingBanner />
        </div>

        <div className="flex-1">
          <div className="mx-auto px-3 sm:px-4 lg:px-6 pt-4 pb-24">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_18rem] lg:grid-cols-[18rem_1fr_18rem] gap-4">
              <CustomerLeftSideBar title={leftSidebarSlot.title}>
                {leftSidebarSlot.content}
              </CustomerLeftSideBar>

              <div className="md:col-start-1 md:row-start-1 lg:col-start-2 lg:row-start-1 min-w-0">
                <div className="pt-4">
                  <Outlet />
                </div>
              </div>

              <div className="md:col-start-2 md:row-start-1 lg:col-start-3 lg:row-start-1">
                <CustomerRightSideBar
                  activeKey={activeKey}
                  onNavigateKey={onNavigateKey}
                  onOpenAuth={() => setAuthOpen(true)}
                  mobileOpen={rightSidebarOpen}
                  onCloseMobile={() => setRightSidebarOpen(false)}
                  user={user}
                  isInitializing={isInitializing}
                />
              </div>
            </div>
          </div>
        </div>

        <Footer />

        <AuthModal isOpen={authOpen} onClose={() => { setAuthOpen(false); setAuthInitialMode('login') }} initialMode={authInitialMode} />
        <ReferralBanner onOpenAuth={() => { setAuthInitialMode('register'); setAuthOpen(true) }} />
      </div>
    </CustomerLeftSidebarSlotProvider>
  )
}
